import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { AppConfigService } from '~/core/services/appconfig.service';
import { MsalService } from '@azure/msal-angular';
import { UsageAnalyticsResponseApi } from './user-dashboard.models';

interface UsageAnalyticsCacheRecord {
    cachedAt: number;
    response: UsageAnalyticsResponseApi;
}

@Injectable({
    providedIn: 'root'
})
export class UsageAnalyticsService {
    private readonly endpoint = '/analytics/usage';
    private readonly cacheStorageKey = 'intel-engine-usage-analytics-cache-v1';
    private readonly cacheTtlMs = 5 * 60 * 1000;
    private apiBaseUrl = '';

    constructor(
        private http: HttpClient,
        private appConfigService: AppConfigService,
        private authService: MsalService
    ) {
        this.apiBaseUrl = (this.appConfigService.getConfig('apiUrl') || '').replace(/\/$/, '');
    }

    public getUsageAnalytics(startDate?: string, endDate?: string): Observable<UsageAnalyticsResponseApi> {
        let params = new HttpParams();

        if (startDate?.trim()) {
            params = params.set('start_date', startDate.trim());
        }

        if (endDate?.trim()) {
            params = params.set('end_date', endDate.trim());
        }

        return this.http.get<UsageAnalyticsResponseApi>(
            `${this.apiBaseUrl}${this.endpoint}`,
            {
                headers: this.buildHeaders(),
                params
            }
        );
    }

    public getCachedUsageAnalytics(startDate?: string, endDate?: string): UsageAnalyticsResponseApi | null {
        try {
            const rawCache = sessionStorage.getItem(this.cacheStorageKey);

            if (!rawCache) {
                return null;
            }

            const parsedCache = JSON.parse(rawCache) as Record<string, UsageAnalyticsCacheRecord>;
            if (!parsedCache || typeof parsedCache !== 'object') {
                return null;
            }

            const cacheKey = this.getCacheKey(startDate, endDate);
            const record = parsedCache[cacheKey];

            if (!record || !record.response || typeof record.cachedAt !== 'number') {
                return null;
            }

            const isExpired = Date.now() - record.cachedAt > this.cacheTtlMs;
            if (isExpired) {
                delete parsedCache[cacheKey];
                sessionStorage.setItem(this.cacheStorageKey, JSON.stringify(parsedCache));
                return null;
            }

            return record.response;
        } catch {
            return null;
        }
    }

    public cacheUsageAnalytics(response: UsageAnalyticsResponseApi, startDate?: string, endDate?: string): void {
        try {
            const rawCache = sessionStorage.getItem(this.cacheStorageKey);
            const parsedCache = rawCache ? JSON.parse(rawCache) as Record<string, UsageAnalyticsCacheRecord> : {};

            const cacheKey = this.getCacheKey(startDate, endDate);
            parsedCache[cacheKey] = {
                cachedAt: Date.now(),
                response
            };

            sessionStorage.setItem(this.cacheStorageKey, JSON.stringify(parsedCache));
        } catch {
        }
    }

    private buildHeaders(): HttpHeaders {
        const token = this.authService.instance.getActiveAccount()?.idToken;
        let headers = new HttpHeaders({
            Accept: 'application/json'
        });

        if (token) {
            headers = headers.set('Authorization', `Bearer ${token}`);
        }

        return headers;
    }

    private getCacheKey(startDate?: string, endDate?: string): string {
        const normalizedStartDate = startDate?.trim() ?? '';
        const normalizedEndDate = endDate?.trim() ?? '';
        return `${normalizedStartDate}__${normalizedEndDate}`;
    }
}
